/**
 * «برداشت رمزارز» operations (Phase 5; PRODUCT_REQUIREMENTS §8.3). Confirmed requests only; the total
 * (amount + network fee) is already RESERVED on the ledger. The admin sends manually and records:
 *   COMPLETE (payout TXID; screening gate) → reserve consumed: amount → chain clearing, fee → fee revenue;
 *   REJECT → the full reserved total returns (exclusive with COMPLETE, DB-enforced);
 *   HOLD / RESUME → security hold, nothing moves; RESCREEN → new append-only screening check.
 * The user only ever sees «در حال انجام» until the outcome.
 */
import {
  AppContainer,
  Button,
  DataTable,
  PageHeader,
  SelectField,
  StateView,
  TextField,
} from '@hopex/ui';
import { useState } from 'react';
import { api, dt, newIdempotencyKey, type Paged } from '../../api.ts';
import { CommandDialog, Facts } from '../../lib/command.tsx';
import {
  CRYPTO_WITHDRAWAL_STATUS,
  exact,
  type CryptoWithdrawalRow,
} from '../../lib/crypto-model.ts';
import { Remote, useRemote } from '../../lib/remote.tsx';
import { TechCode } from '../../lib/ui.tsx';
import { Counter, num, Pager, Tone, UserCell } from '../money/shared.tsx';
import { LongId, ScreeningCell } from './shared.tsx';

type Act = 'COMPLETE' | 'REJECT' | 'HOLD' | 'RESUME' | 'RESCREEN';
const LABEL: Record<Act, string> = {
  COMPLETE: 'ثبت ارسال',
  REJECT: 'رد و بازگشت',
  HOLD: 'توقف امنیتی',
  RESUME: 'ادامه',
  RESCREEN: 'غربالگری دوباره',
};
const ACTS: Record<string, Act[]> = {
  PROCESSING: ['COMPLETE', 'HOLD', 'RESCREEN', 'REJECT'],
  SECURITY_HOLD: ['RESUME', 'RESCREEN', 'REJECT'],
};
const EFFECT: Record<Act, string> = {
  COMPLETE:
    'مبلغ رزروشده یک‌بار مصرف می‌شود: مقدار مقصد به حساب تسویه‌ی زنجیره و کارمزد به درآمد کارمزد.',
  REJECT: 'کل مبلغ رزروشده (مقدار + کارمزد) به موجودی قابل استفاده‌ی کاربر برمی‌گردد.',
  HOLD: 'پردازش متوقف می‌شود؛ مبلغ رزرو می‌ماند.',
  RESUME: 'پردازش ادامه پیدا می‌کند؛ مبلغ رزرو می‌ماند.',
  RESCREEN: 'یک بررسی ریسک تازه برای آدرس مقصد ثبت می‌شود؛ پولی جابه‌جا نمی‌شود.',
};

function ActDialog({
  w,
  act,
  onClose,
  onDone,
}: {
  w: CryptoWithdrawalRow;
  act: Act;
  onClose: () => void;
  onDone: () => void;
}) {
  const [key] = useState(newIdempotencyKey);
  const [txid, setTxid] = useState('');
  const [accept, setAccept] = useState(false);
  const needsAccept = w.screening.state === 'NEEDS_ACCEPTANCE';
  const blocked = w.screening.state === 'BLOCKED';
  return (
    <CommandDialog
      title={`${LABEL[act]} — ${w.ref}`}
      tone={
        act === 'COMPLETE' || act === 'RESUME'
          ? 'positive'
          : act === 'REJECT'
            ? 'danger'
            : act === 'HOLD'
              ? 'caution'
              : 'primary'
      }
      confirmLabel={LABEL[act]}
      reasonRequired={act !== 'RESCREEN'}
      canSubmit={
        act !== 'COMPLETE' || (!blocked && txid.trim().length >= 8 && (!needsAccept || accept))
      }
      run={async (reason) => {
        if (act === 'RESCREEN')
          return void (await api.post(`/crypto-withdrawals/${w.id}/rescreen`));
        await api.post(
          `/crypto-withdrawals/${w.id}/action`,
          {
            action: act,
            reason,
            expectedVersion: w.version,
            ...(act === 'COMPLETE'
              ? { payoutTxid: txid.trim(), acceptScreeningReview: accept }
              : {}),
          },
          { 'idempotency-key': key },
        );
      }}
      onClose={onClose}
      onDone={onDone}
      data-crypto-withdrawal-action={act}
    >
      <Facts
        rows={[
          ['کاربر', w.displayName ?? w.publicId],
          ['دارایی / شبکه', `${w.asset} · ${w.networkName}`],
          ['آدرس مقصد', <LongId key="a" value={w.address} href={w.addressExplorerUrl} />],
          ...(w.memo
            ? ([
                [
                  'ممو',
                  <bdi key="m" dir="ltr">
                    {w.memo}
                  </bdi>,
                ],
              ] as [string, React.ReactNode][])
            : []),
          [
            'مقدار مقصد',
            <bdi key="d" dir="ltr" className="font-mono font-bold">
              {exact(w.destAmount, w.asset)}
            </bdi>,
          ],
          [
            'کارمزد شبکه',
            <bdi key="f" dir="ltr" className="font-mono">
              {exact(w.fee, w.asset)}
            </bdi>,
          ],
          [
            'مجموع رزروشده',
            <bdi key="t" dir="ltr" className="font-mono">
              {exact(w.total, w.asset)}
            </bdi>,
          ],
          ['غربالگری مقصد', <ScreeningCell key="s" s={w.screening} />],
          ['اثر', EFFECT[act]],
        ]}
      />
      {act === 'COMPLETE' && (
        <div className="grid gap-2">
          <TextField label="TXID ارسال (الزامی)" value={txid} onChange={setTxid} ltr />
          {blocked && (
            <p className="m-0 text-[13px] text-negative" data-screening-blocked>
              ثبت ارسال ممکن نیست: نتیجه‌ی غربالگری مقصد اجازه نمی‌دهد ({w.screening.reason}).
            </p>
          )}
          {needsAccept && (
            <label className="flex items-start gap-2 text-[13px]">
              <input
                type="checkbox"
                checked={accept}
                onChange={(e) => setAccept(e.target.checked)}
                className="mt-1"
                data-accept-review
              />
              نتیجه‌ی «نیازمند بررسی» مقصد را بررسی کردم و با همین دلیل می‌پذیرم.
            </label>
          )}
        </div>
      )}
    </CommandDialog>
  );
}

export function CryptoWithdrawalsPage() {
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<{ w: CryptoWithdrawalRow; act: Act } | null>(null);
  const q = new URLSearchParams({ page: String(page) });
  if (status) q.set('status', status);
  const r = useRemote<Paged<CryptoWithdrawalRow>>(`/crypto-withdrawals?${q.toString()}`);
  return (
    <AppContainer>
      <PageHeader title="برداشت رمزارز" />
      <div className="mb-3 max-w-xs">
        <SelectField
          label="وضعیت"
          value={status}
          onChange={(v) => (setStatus(v), setPage(1))}
          options={[
            { value: '', label: 'همه' },
            ...Object.entries(CRYPTO_WITHDRAWAL_STATUS).map(([k, v]) => ({
              value: k,
              label: v.label,
            })),
          ]}
        />
      </div>
      <Remote r={r}>
        {(d) => (
          <div className="flex flex-col gap-3" data-crypto-withdrawal-queue>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {Object.entries(CRYPTO_WITHDRAWAL_STATUS).map(([k, v]) => (
                <Counter
                  key={k}
                  label={v.label}
                  value={num(d.counts?.[k])}
                  {...(k === 'SECURITY_HOLD' && d.counts?.[k] ? { tone: 'alert' } : {})}
                />
              ))}
            </div>
            <DataTable<CryptoWithdrawalRow>
              label="برداشت‌های رمزارز"
              rows={d.items}
              density="compact"
              stickyFirst
              columns={[
                {
                  id: 'ref',
                  header: 'کد',
                  isRowHeader: true,
                  cell: (x) => (
                    <bdi dir="ltr" className="font-mono text-[12px]">
                      {x.ref}
                    </bdi>
                  ),
                },
                {
                  id: 'user',
                  header: 'کاربر',
                  cell: (x) => (
                    <UserCell
                      userId={x.userId}
                      name={x.displayName}
                      publicId={x.publicId}
                      tab="crypto"
                    />
                  ),
                },
                {
                  id: 'dest',
                  header: 'مقصد',
                  cell: (x) => (
                    <span className="flex flex-col text-[12px]">
                      <span>
                        <bdi dir="ltr" className="font-semibold">
                          {x.asset}
                        </bdi>{' '}
                        · {x.networkName}
                      </span>
                      <LongId value={x.address} href={x.addressExplorerUrl} />
                      {x.memo && (
                        <span className="text-fg-subtle">
                          ممو: <bdi dir="ltr">{x.memo}</bdi>
                        </span>
                      )}
                    </span>
                  ),
                },
                {
                  id: 'amount',
                  header: 'مقدار / کارمزد',
                  numeric: true,
                  cell: (x) => (
                    <span className="flex flex-col text-[12px]">
                      <bdi dir="ltr" className="font-mono font-semibold">
                        {exact(x.destAmount, x.asset)}
                      </bdi>
                      <bdi dir="ltr" className="font-mono text-fg-subtle">
                        + {exact(x.fee, x.asset)}
                      </bdi>
                    </span>
                  ),
                },
                {
                  id: 'screen',
                  header: 'غربالگری',
                  cell: (x) => <ScreeningCell s={x.screening} />,
                },
                {
                  id: 'st',
                  header: 'وضعیت',
                  cell: (x) => (
                    <span className="flex flex-col items-start gap-0.5 text-[12px]">
                      <Tone map={CRYPTO_WITHDRAWAL_STATUS} code={x.status} />
                      {x.holdReason && <span className="text-fg-subtle">{x.holdReason}</span>}
                      {x.payoutTxid && <LongId value={x.payoutTxid} href={x.payoutExplorerUrl} />}
                      {x.rejectReason && <span className="text-fg-subtle">{x.rejectReason}</span>}
                      <span className="text-fg-subtle">{dt(x.confirmedAt ?? x.createdAt)}</span>
                    </span>
                  ),
                },
                {
                  id: 'tx',
                  header: 'اسناد',
                  cell: (x) => (
                    <details className="text-[12px]">
                      <summary className="cursor-pointer text-fg-muted">شناسه</summary>
                      <TechCode>reserve: {x.reserveTransactionId ?? '—'}</TechCode>
                      <TechCode>settle: {x.settleTransactionId ?? '—'}</TechCode>
                    </details>
                  ),
                },
                {
                  id: 'act',
                  header: 'عملیات',
                  cell: (x) => (
                    <div className="flex flex-wrap gap-1">
                      {(ACTS[x.status] ?? []).map((a) => (
                        <Button
                          key={a}
                          size="sm"
                          variant={a === 'COMPLETE' ? 'secondary' : 'ghost'}
                          onPress={() => setOpen({ w: x, act: a })}
                          data-crypto-withdrawal-act={`${x.ref}:${a}`}
                        >
                          {LABEL[a]}
                        </Button>
                      ))}
                    </div>
                  ),
                },
              ]}
              empty={<StateView compact kind="empty" title="برداشت رمزارزی با این فیلتر نیست" />}
            />
            <Pager page={d.page} hasMore={d.hasMore} onPage={setPage} />
          </div>
        )}
      </Remote>
      {open && (
        <ActDialog
          w={open.w}
          act={open.act}
          onClose={() => setOpen(null)}
          onDone={() => (setOpen(null), r.reload())}
        />
      )}
    </AppContainer>
  );
}
